import React, { useState, useEffect, useCallback } from 'react';
import { Calendar, Plus, X, Bell, Clock, MapPin } from 'lucide-react';

const App = () => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [events, setEvents] = useState({});
  const [selectedDate, setSelectedDate] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [newEvent, setNewEvent] = useState({
    title: '',
    description: '',
    time: '',
    location: '',
    color: '#3b82f6'
  });

  // Mock initial events
  useEffect(() => {
    const initialEvents = {
      '2025-12-21': [
        { id: 1, title: 'Рождество', description: 'Праздник Рождества Христова', time: '10:00', location: 'Церковь Святого Николая', color: '#ef4444' }
      ],
      '2025-12-25': [
        { id: 2, title: 'Встреча с друзьями', description: 'Новогодняя вечеринка', time: '19:00', location: 'Ресторан "Зимняя сказка"', color: '#10b981' }
      ],
      '2025-12-31': [
        { id: 3, title: 'Новый Год', description: 'Празднование Нового Года', time: '00:00', location: 'Дома', color: '#8b5cf6' }
      ]
    };
    setEvents(initialEvents);
  }, []);

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];
    
    // Previous month days
    const prevMonthLastDay = new Date(year, month, 0).getDate();
    for (let i = startingDayOfWeek - 1; i >= 0; i--) {
      const day = prevMonthLastDay - i;
      const prevDate = new Date(year, month - 1, day);
      days.push({
        date: prevDate,
        day: day,
        isCurrentMonth: false,
        isToday: isSameDay(prevDate, new Date())
      });
    }

    // Current month days
    for (let day = 1; day <= daysInMonth; day++) {
      const currDate = new Date(year, month, day);
      days.push({
        date: currDate,
        day: day,
        isCurrentMonth: true,
        isToday: isSameDay(currDate, new Date())
      });
    }

    // Next month days
    const totalCells = 42; // 6 weeks * 7 days
    const nextDaysNeeded = totalCells - days.length;
    for (let day = 1; day <= nextDaysNeeded; day++) {
      const nextDate = new Date(year, month + 1, day);
      days.push({
        date: nextDate,
        day: day,
        isCurrentMonth: false,
        isToday: isSameDay(nextDate, new Date())
      });
    }

    return days;
  };

  const isSameDay = (date1, date2) => {
    return date1.getFullYear() === date2.getFullYear() &&
           date1.getMonth() === date2.getMonth() &&
           date1.getDate() === date2.getDate();
  };

  const formatDateKey = (date) => {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  };

  const getMonthName = (date) => {
    const months = [
      'Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь',
      'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'
    ];
    return months[date.getMonth()];
  };

  const getDayName = (index) => {
    const days = ['Вс', 'Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб'];
    return days[index];
  };

  const navigateMonth = (direction) => {
    setCurrentDate(prev => {
      const newDate = new Date(prev);
      newDate.setMonth(prev.getMonth() + direction);
      return newDate;
    });
  };

  const handleDateClick = (date) => {
    setSelectedDate(date);
    setNewEvent({
      title: '',
      description: '',
      time: '',
      location: '',
      color: '#3b82f6'
    });
    setShowModal(true);
  };

  const handleAddEvent = () => {
    if (!newEvent.title.trim()) return;

    const dateKey = formatDateKey(selectedDate);
    const event = {
      id: Date.now(),
      ...newEvent
    };

    setEvents(prev => ({
      ...prev,
      [dateKey]: [...(prev[dateKey] || []), event]
    }));

    setShowModal(false);
  };

  const handleDeleteEvent = (dateKey, eventId) => {
    setEvents(prev => ({
      ...prev,
      [dateKey]: prev[dateKey].filter(event => event.id !== eventId)
    }));
  };

  const days = getDaysInMonth(currentDate);
  const today = new Date();

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 p-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-4">
            <Calendar className="w-8 h-8 text-blue-600" />
            <h1 className="text-3xl md:text-4xl font-bold text-gray-800">Календарь событий</h1>
          </div>
          <p className="text-gray-600">Привязывайте события к конкретным датам и управляйте своим расписанием</p>
        </div>

        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          {/* Calendar Header */}
          <div className="bg-gradient-to-r from-blue-600 to-indigo-700 p-6 text-white">
            <div className="flex items-center justify-between">
              <button
                onClick={() => navigateMonth(-1)}
                className="p-2 rounded-full hover:bg-blue-700 transition-colors"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              </button>
              
              <div className="text-center">
                <h2 className="text-2xl font-bold">{getMonthName(currentDate)} {currentDate.getFullYear()}</h2>
                <p className="text-blue-200">Выберите дату для добавления события</p>
              </div>
              
              <button
                onClick={() => navigateMonth(1)}
                className="p-2 rounded-full hover:bg-blue-700 transition-colors"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                </svg>
              </button>
            </div>
          </div>

          {/* Calendar Grid */}
          <div className="p-6">
            {/* Days of week header */}
            <div className="grid grid-cols-7 gap-1 mb-2">
              {Array.from({ length: 7 }).map((_, i) => (
                <div key={i} className="text-center font-semibold text-gray-700 py-3">
                  {getDayName(i)}
                </div>
              ))}
            </div>

            {/* Calendar days */}
            <div className="grid grid-cols-7 gap-1">
              {days.map((day, index) => {
                const dateKey = formatDateKey(day.date);
                const dayEvents = events[dateKey] || [];
                const hasEvents = dayEvents.length > 0;
                
                return (
                  <div
                    key={index}
                    onClick={() => day.isCurrentMonth && handleDateClick(day.date)}
                    className={`
                      min-h-24 p-2 border rounded-lg cursor-pointer transition-all duration-200
                      ${day.isCurrentMonth 
                        ? 'bg-white hover:bg-blue-50 border-gray-200 hover:border-blue-300' 
                        : 'bg-gray-50 text-gray-400 border-gray-100 cursor-default'
                      }
                      ${day.isToday ? 'ring-2 ring-blue-500 ring-inset' : ''}
                      ${hasEvents && day.isCurrentMonth ? 'border-blue-300 bg-blue-50' : ''}
                    `}
                  >
                    <div className={`text-sm font-medium mb-1 ${
                      day.isToday ? 'text-blue-600 font-bold' : 
                      day.isCurrentMonth ? 'text-gray-800' : 'text-gray-400'
                    }`}>
                      {day.day}
                    </div>
                    
                    {/* Events preview */}
                    <div className="space-y-1">
                      {dayEvents.slice(0, 2).map((event, i) => (
                        <div
                          key={event.id}
                          className="text-xs p-1 rounded truncate"
                          style={{ backgroundColor: `${event.color}20`, color: event.color }}
                          title={event.title}
                        >
                          {event.title}
                        </div>
                      ))}
                      {dayEvents.length > 2 && (
                        <div className="text-xs text-gray-500">
                          +{dayEvents.length - 2} еще
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Events List for Selected Month */}
          <div className="border-t border-gray-200 p-6 bg-gray-50">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">События в этом месяце</h3>
            <div className="space-y-3">
              {Object.entries(events).filter(([dateKey]) => {
                const eventDate = new Date(dateKey);
                return eventDate.getMonth() === currentDate.getMonth() && 
                       eventDate.getFullYear() === currentDate.getFullYear();
              }).length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Calendar className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p>Нет событий в этом месяце</p>
                  <p className="text-sm mt-1">Кликните на любую дату, чтобы добавить событие</p>
                </div>
              ) : (
                Object.entries(events)
                  .filter(([dateKey]) => {
                    const eventDate = new Date(dateKey);
                    return eventDate.getMonth() === currentDate.getMonth() && 
                           eventDate.getFullYear() === currentDate.getFullYear();
                  })
                  .flatMap(([dateKey, dayEvents]) => 
                    dayEvents.map(event => ({ ...event, date: dateKey }))
                  )
                  .sort((a, b) => new Date(a.date) - new Date(b.date))
                  .map((event) => (
                    <div
                      key={event.id}
                      className="bg-white p-4 rounded-lg border border-gray-200 hover:shadow-md transition-shadow"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <div 
                              className="w-3 h-3 rounded-full"
                              style={{ backgroundColor: event.color }}
                            ></div>
                            <h4 className="font-semibold text-gray-800">{event.title}</h4>
                          </div>
                          <p className="text-sm text-gray-600 mb-2">{event.description}</p>
                          <div className="flex flex-wrap gap-2 text-xs text-gray-500">
                            <div className="flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {event.time || 'Весь день'}
                            </div>
                            {event.location && (
                              <div className="flex items-center gap-1">
                                <MapPin className="w-3 h-3" />
                                {event.location}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-sm font-medium text-gray-700 mb-1">
                            {new Date(event.date).toLocaleDateString('ru-RU', { 
                              day: 'numeric', 
                              month: 'short',
                              weekday: 'short'
                            })}
                          </div>
                          <button
                            onClick={() => handleDeleteEvent(event.date, event.id)}
                            className="p-1 text-red-500 hover:bg-red-50 rounded transition-colors"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))
              )}
            </div>
          </div>
        </div>

        {/* Add Event Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-xl font-bold text-gray-800">Добавить событие</h3>
                  <button
                    onClick={() => setShowModal(false)}
                    className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Дата
                    </label>
                    <div className="text-lg font-medium text-gray-800">
                      {selectedDate?.toLocaleDateString('ru-RU', { 
                        weekday: 'long', 
                        year: 'numeric', 
                        month: 'long', 
                        day: 'numeric' 
                      })}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Название события *
                    </label>
                    <input
                      type="text"
                      value={newEvent.title}
                      onChange={(e) => setNewEvent(prev => ({ ...prev, title: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Введите название события"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Описание
                    </label>
                    <textarea
                      value={newEvent.description}
                      onChange={(e) => setNewEvent(prev => ({ ...prev, description: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Описание события (необязательно)"
                      rows="3"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Время
                      </label>
                      <input
                        type="time"
                        value={newEvent.time}
                        onChange={(e) => setNewEvent(prev => ({ ...prev, time: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Место
                      </label>
                      <input
                        type="text"
                        value={newEvent.location}
                        onChange={(e) => setNewEvent(prev => ({ ...prev, location: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Место проведения"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Цвет события
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {['#3b82f6', '#10b981', '#ef4444', '#8b5cf6', '#f59e0b', '#6b7280'].map((color) => (
                        <button
                          key={color}
                          onClick={() => setNewEvent(prev => ({ ...prev, color }))}
                          className={`w-8 h-8 rounded-full border-2 transition-all ${
                            newEvent.color === color ? 'border-gray-400 scale-110' : 'border-gray-200'
                          }`}
                          style={{ backgroundColor: color }}
                          title={color}
                        />
                      ))}
                    </div>
                  </div>
                </div>

                <div className="mt-8 flex gap-3">
                  <button
                    onClick={() => setShowModal(false)}
                    className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    Отмена
                  </button>
                  <button
                    onClick={handleAddEvent}
                    disabled={!newEvent.title.trim()}
                    className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
                  >
                    <Plus className="w-4 h-4" />
                    Добавить
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default App;


